import React, { useEffect, useRef, useState } from "react";
import Button from "./controls/Button";
import * as faceapi from "face-api.js";
import {
  Box,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ReusableDialog from "./controls/ReusableDialog";
import quiz from "../redux/actions/quiz";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";

const FACE_SCORE_ALLOWANCE = 0.8;
const FACE_COVERAGE_ALLOWANCE = 40;
const EYES_ALIGNED_ALLOWANCE = 10;
const V_SYMMETRICAL_ALLOWANCE = 10;
const NOSE_CENTERED_ALLOWANCE = 10;

const COUNTDOWN_SECONDS = 3;

const FaceDetection = ({ open, setOpen }) => {
  const dispatch = useDispatch();
  const roleState = useSelector((state) => state.role);
  const quizState = useSelector((state) => state.quiz);

  const videoRef = useRef(null);
  const [isVideoReady, setIsVideoReady] = useState(false);

  const [faceDetected, setFaceDetected] = useState(false);
  const [faceScore, setFaceScore] = useState(0);
  const [multipleFacesDetected, setMultipleFacesDetected] = useState(false);
  const [faceCoverage, setFaceCoverage] = useState(0);
  const [isLookingForward, setIsLookingForward] = useState(false);

  const candidateId = roleState?.candidateDashboardDetails?.candidate?.user?.uid;
  // const roleId = roleState?.candidateDashboardDetails?.jobRole?.uid;
  const inviteId = roleState?.candidateDashboardDetails?.uid;

  const countdownRef = useRef(null); // Tracks countdown number outside React state
  const countdownInterval = useRef(null);
  const [countdown, setCountdown] = useState(null);
  const [snapTaken, setSnapTaken] = useState(false);

  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState(null);

  const lastStats = useRef({});

  const [statusMessage, setStatusMessage] = useState("");
  const [capturedImage, setCapturedImage] = useState(null);

  useEffect(() => {
    let intervalId;
    if (open) {
      setSnapTaken(false);
      intervalId = setInterval(detectFace, 500); // Check every 500ms
    }
    return () => clearInterval(intervalId);
  }, [open]);

  useEffect(() => {
    if (open) {
      startCamera();
      loadModels();
    }
    return () => {
      stopCamera();
    };
  }, [open]);

  const loadModels = async () => {
    try {
      await faceapi.nets.tinyFaceDetector.loadFromUri("/models");
      await faceapi.nets.faceLandmark68Net.loadFromUri("/models");
      await faceapi.nets.faceRecognitionNet.loadFromUri("/models");
      await faceapi.nets.faceExpressionNet.loadFromUri("/models");
      startCamera();
    } catch (error) {
      console.error("Error loading face-api.js models:", error);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error("Error accessing the camera:", error);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  const handleTakeSnap = () => {
    if (videoRef.current) {
      const canvas = document.createElement("canvas");
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      const imageData = canvas.toDataURL("image/jpeg", 0.7);
      setCapturedImage(imageData);
      stopCamera();
      setStatusMessage("image captured successfully");
      const payload = { candidateId, inviteId, imageData };
      dispatch(quiz.uploadCandidatePrimaryPhoto(payload));
    }
  };

  // This function analyzes the alignment of the eyes and nose to determine if the user is looking forward.
  const detectFace = async () => {
    if (!videoRef.current) return;
    const videoElement = videoRef.current;

    // Step 1: Detect faces using TinyFaceDetector
    const faceDetections = await faceapi.detectAllFaces(videoElement, new faceapi.TinyFaceDetectorOptions());

    if (faceDetections.length > 0) {
      const videoWidth = videoElement.videoWidth;
      const videoHeight = videoElement.videoHeight;

      // Define the circular center region
      const centerX = videoWidth / 2;
      const centerY = videoHeight / 2;
      const radius = Math.min(videoWidth, videoHeight) * 0.35; // Radius matches the transparent center of the gradient (35%)

      // Filter detections to check if they fall within the circular region
      const centerFace = faceDetections.find((detection) => {
        const { x, y, width, height } = detection.box;
        const faceCenterX = x + width / 2;
        const faceCenterY = y + height / 2;

        // Check if the face center is within the circular region
        const distance = Math.sqrt(Math.pow(faceCenterX - centerX, 2) + Math.pow(faceCenterY - centerY, 2));
        return distance <= radius;
      });

      if (centerFace) {
        const faceArea = centerFace.box.width * centerFace.box.height;

        // Calculate the percentage of the circular region covered by the face
        const circularArea = Math.PI * Math.pow(radius, 2);
        const faceCoverage = (faceArea / circularArea) * 100;
        setFaceCoverage(faceCoverage);

        // Step 2: Analyze landmarks for the detected face
        const landmarks = await faceapi.detectFaceLandmarks(videoElement);

        // console.log(landmarks, "landmarks");

        if (landmarks) {
          const leftEye = landmarks.getLeftEye();
          const rightEye = landmarks.getRightEye();
          const nose = landmarks.getNose();

          // console.log(leftEye, rightEye, "leftEye rightEye");

          // Calculate stats for debugging
          const leftEyeCenter = {
            x: leftEye.reduce((sum, point) => sum + point.x, 0) / leftEye.length,
            y: leftEye.reduce((sum, point) => sum + point.y, 0) / leftEye.length,
          };
          const rightEyeCenter = {
            x: rightEye.reduce((sum, point) => sum + point.x, 0) / rightEye.length,
            y: rightEye.reduce((sum, point) => sum + point.y, 0) / rightEye.length,
          };
          const noseCenter = {
            x: nose.reduce((sum, point) => sum + point.x, 0) / nose.length,
            y: nose.reduce((sum, point) => sum + point.y, 0) / nose.length,
          };

          const eyeAlignmentPercentage = (Math.abs(leftEyeCenter.y - rightEyeCenter.y) / videoHeight) * 100;
          const getDistance = (a, b) => Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2));
          const noseSymmetryPercentage =
            (Math.abs(Math.abs(noseCenter.x - leftEyeCenter.x) - Math.abs(noseCenter.x - rightEyeCenter.x)) /
              videoWidth) *
            100;
          const vSymmetryPercentage =
            (Math.abs(getDistance(noseCenter, leftEyeCenter) - getDistance(noseCenter, rightEyeCenter)) / videoWidth) *
            100;

          const isEyesAligned = eyeAlignmentPercentage < EYES_ALIGNED_ALLOWANCE;
          const isNoseCentered = noseSymmetryPercentage < NOSE_CENTERED_ALLOWANCE;
          const isVSymmetrical = vSymmetryPercentage < V_SYMMETRICAL_ALLOWANCE;

          lastStats.current = {
            faceScore: Math.round(centerFace.score * 100),
            faceScorePass: centerFace.score >= FACE_SCORE_ALLOWANCE,
            faceCoverage: faceCoverage.toFixed(2),
            faceCoveragePass: faceCoverage >= FACE_COVERAGE_ALLOWANCE,
            isLookingForward: isEyesAligned && isVSymmetrical && isNoseCentered,
            eyeAlignmentPercentage: eyeAlignmentPercentage.toFixed(2),
            noseSymmetryPercentage: noseSymmetryPercentage.toFixed(2),
            vSymmetryPercentage: vSymmetryPercentage.toFixed(2),
            isEyesAligned,
            isVSymmetrical,
            isNoseCentered,
            multipleFacesDetected: faceDetections.length > 1,
          };

          const isForward = isEyesAligned && isVSymmetrical && isNoseCentered;
          setIsLookingForward(isForward);

          if (!isForward) {
            setStatusMessage("Try to hold your face straight and look into the camera. Adjust lighting if needed.");
          } else if (faceCoverage < FACE_COVERAGE_ALLOWANCE) {
            setStatusMessage("Move closer to the camera to improve face coverage.");
          }

          // Update face detection state
          setFaceScore(Math.round(centerFace.score * 100));
          setFaceDetected(
            centerFace.score >= FACE_SCORE_ALLOWANCE && faceCoverage >= FACE_COVERAGE_ALLOWANCE && isForward
          ); // Valid face if score >= 75%, coverage >= 20%, and looking forward
          setMultipleFacesDetected(faceDetections.length > 1);

          // countdown logic
          const shouldStartCountdown =
            centerFace.score >= FACE_SCORE_ALLOWANCE &&
            faceCoverage >= FACE_COVERAGE_ALLOWANCE &&
            isForward &&
            faceDetections.length <= 1;

          if (shouldStartCountdown && countdownRef.current === null) {
            startCountdown();
          }

          // cancel countdown if conditions are not met
          if (!shouldStartCountdown && countdownRef.current !== null) {
            stopCountdown();
          }
        } else {
          setStatusMessage("Try to center your face in the frame.");
          resetFaceState();
        }
      } else {
        setStatusMessage("Try to center your face in the frame.");
        resetFaceState();
      }
    } else {
      setStatusMessage("No face detected. Please position your face in front of the camera.");
      resetFaceState();
    }
  };

  const startCountdown = () => {
    countdownRef.current = COUNTDOWN_SECONDS;
    setCountdown(COUNTDOWN_SECONDS);

    countdownInterval.current = setInterval(() => {
      countdownRef.current -= 1;
      setCountdown((prev) => {
        if (prev === 0) {
          clearInterval(countdownInterval.current);
          countdownRef.current = null;
          setSnapTaken(true);
          handleTakeSnap();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopCountdown = () => {
    clearInterval(countdownInterval.current);
    countdownRef.current = null;
    setCountdown(null);
  };

  const resetFaceState = () => {
    if (countdownRef.current !== null) {
      clearInterval(countdownInterval.current);
      countdownRef.current = null;
      setCountdown(null);
    }
    setFaceDetected(false);
    setFaceScore(0);
    setFaceCoverage(0);
    setIsLookingForward(false);
    setMultipleFacesDetected(false);
  };

  // This function analyzes the alignment of the eyes and nose to determine if the user is looking forward.
  // const checkLookingForward = (leftEye, rightEye, nose) => {
  //   // Calculate the average position of the left eye
  //   const leftEyeCenter = {
  //     x: leftEye.reduce((sum, point) => sum + point._x, 0) / leftEye.length,
  //     y: leftEye.reduce((sum, point) => sum + point._y, 0) / leftEye.length,
  //   };

  //   // Calculate the average position of the right eye
  //   const rightEyeCenter = {
  //     x: rightEye.reduce((sum, point) => sum + point._x, 0) / rightEye.length,
  //     y: rightEye.reduce((sum, point) => sum + point._y, 0) / rightEye.length,
  //   };

  //   // Calculate the average position of the nose
  //   const noseCenter = {
  //     x: nose.reduce((sum, point) => sum + point._x, 0) / nose.length,
  //     y: nose.reduce((sum, point) => sum + point._y, 0) / nose.length,
  //   };

  //   // Calculate eye alignment (horizontal line check)
  //   const eyeAlignment = Math.abs(leftEyeCenter.y - rightEyeCenter.y);

  //   // Calculate the symmetry of the nose relative to the eyes
  //   const noseToLeftEye = Math.abs(noseCenter.x - leftEyeCenter.x);
  //   const noseToRightEye = Math.abs(noseCenter.x - rightEyeCenter.x);

  //   // V-shape symmetry: distance from nose to each eye center
  //   const getDistance = (a, b) => Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2));
  //   const distToLeft = getDistance(noseCenter, leftEyeCenter);
  //   const distToRight = getDistance(noseCenter, rightEyeCenter);

  //   const vDiff = Math.abs(distToLeft - distToRight);
  //   // const avgDist = (distToLeft + distToRight) / 2;
  //   // const symmetryThreshold = avgDist * 0.2; // 10% tolerance

  //   // Final checks
  //   const isEyesAligned = eyeAlignment < 40; // You can tweak this threshold
  //   const isVSymmetrical = vDiff < 40;
  //   const isNoseCentered = Math.abs(noseToLeftEye - noseToRightEye) < 30; // Adjust threshold as needed

  //   // Debug logs
  //   // console.log("Left Eye Center:", leftEyeCenter);
  //   // console.log("Right Eye Center:", rightEyeCenter);
  //   // console.log("Nose Center:", noseCenter);
  //   // console.log("Eye Alignment:", eyeAlignment);
  //   // console.log("Dist to Left Eye:", distToLeft);
  //   // console.log("Dist to Right Eye:", distToRight);
  //   // console.log("V-Shape Difference:", vDiff);
  //   // // console.log("Is Nose Centered:", isNoseCentered);
  //   // console.log("Is Nose Centered:", Math.abs(noseToLeftEye - noseToRightEye));

  //   return isEyesAligned && isVSymmetrical && isNoseCentered;
  // };

  // Add a circular overlay for the center region
  const CenterOverlay = () => {
    return (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "98%",
          pointerEvents: "none", // Ensure it doesn't block interactions
          zIndex: 2,
          background: `radial-gradient(circle at center, transparent 38%, rgba(0, 0, 0, 0.6) 31%)`, // Transparent center with gradient outside
          borderRadius: "8px", // Match the video border radius
        }}
      />
    );
  };

  // Countdown effect
  // useEffect(() => {
  //   if (countdown === null || snapTaken) return;
  //   if (countdown === 0) {
  //     handleTakeSnap();
  //     setSnapTaken(true);
  //     setCountdown(null);
  //     return;
  //   }
  //   const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
  //   return () => clearTimeout(timer);
  // }, [countdown, snapTaken]);

  useEffect(() => {
    if (quizState?.uploadCandidatePrimaryPhotoSuccess) {
      stopCamera();
      setOpen(false);
    }
  }, [quizState?.uploadCandidatePrimaryPhotoSuccess]);

  const handleLogStats = () => {
    setStats(lastStats.current);
    setShowStats(true);
    console.table(lastStats.current); // Also log to browser console
  };

  const onClose = () => {
    setOpen(false);
    stopCamera();
  };

  useEffect(() => {
    return () => {
      clearInterval(countdownInterval.current);
    };
  }, []);

  return (
    <ReusableDialog
      open={open}
      onClose={() => onClose()}
      // title="Face Detection"
      title={
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Face Detection</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      }
      content={
        <Box
          sx={{
            width: "100%", // Ensure the content takes full width
            maxWidth: "600px", // Limit the maximum width
            margin: "0 auto", // Center the content horizontally
            textAlign: "center", // Center-align text
          }}
        >
          {/* Countdown and Status Message */}
          <Box sx={{ mt: 2, height: "5vh" }}>
            {countdown !== null && !snapTaken ? (
              <Typography sx={{ color: "#787878", fontWeight: "bold", fontSize: 32 }}>
                ⏳ Hold Still... {countdown}
              </Typography>
            ) : (
              <Typography sx={{ color: "#787878", fontWeight: "bold", fontSize: 16 }}>{statusMessage}</Typography>
            )}
          </Box>

          {/* Parent container with relative positioning */}
          <Box sx={{ position: "relative", width: "100%", height: "auto" }}>
            {capturedImage ? (
              <img
                src={capturedImage}
                alt="Captured"
                style={{
                  width: "100%",
                  height: "auto",
                  borderRadius: "8px",
                }}
              />
            ) : (
              <>
                {!isVideoReady && (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    animation="wave"
                    sx={{ position: "absolute", top: 0, left: 0, zIndex: 1 }}
                  />
                )}
                {/* Video Element */}
                <video
                  ref={videoRef}
                  autoPlay
                  onCanPlay={() => setIsVideoReady(true)}
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: "8px",
                  }}
                ></video>

                {/* Circular Overlay */}
                {videoRef.current && <CenterOverlay />}
              </>
            )}
          </Box>

          {/* <Box sx={{ textAlign: "center", mt: 2 }}>
            {multipleFacesDetected ? (
              <Typography sx={{ color: "orange", fontWeight: "bold" }}>Warning: Multiple Faces Detected</Typography>
            ) : faceDetected ? (
              <Typography sx={{ color: "green", fontWeight: "bold" }}>
                Face Detected (Score: {faceScore}%, Coverage: {faceCoverage.toFixed(2)}%, Looking Forward:{" "}
                {isLookingForward ? "Yes" : "No"})
              </Typography>
            ) : (
              <Typography sx={{ color: "red", fontWeight: "bold" }}>
                No Valid Face Detected (Score: {faceScore}%, Coverage: {faceCoverage.toFixed(2)}%, Looking Forward:{" "}
                {isLookingForward ? "Yes" : "No"})
              </Typography>
            )}
          </Box>

          
          {countdown !== null && !snapTaken ? (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ color: "blue", fontWeight: "bold", fontSize: 20 }}>
                Hold Still... {countdown}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ color: "blue", fontWeight: "bold", fontSize: 20 }}>{statusMessage}</Typography>
            </Box>
          )} */}

          <Box sx={{ textAlign: "center", mt: 2 }}>
            {multipleFacesDetected ? (
              <Typography sx={{ color: "orange", fontWeight: "bold" }}>
                ⚠️ Multiple Faces Detected – Please ensure only your face is visible.
              </Typography>
            ) : faceDetected ? (
              <Typography sx={{ color: "green", fontWeight: "bold" }}>
                ✅ Face Detected
                <br />
                Visibility: {faceScore}% –{" "}
                {faceScore < FACE_SCORE_ALLOWANCE * 100
                  ? "Face visibility is too low."
                  : faceScore <= FACE_SCORE_ALLOWANCE * 100
                  ? "Face visibility is acceptable."
                  : "Face visibility is optimal."}
                <br />
                Frame Coverage: {faceCoverage.toFixed(2)}% –{" "}
                {faceCoverage < FACE_COVERAGE_ALLOWANCE
                  ? "Move closer to the camera."
                  : faceCoverage > FACE_COVERAGE_ALLOWANCE
                  ? "Face coverage is good."
                  : faceCoverage > 100
                  ? "Move slightly back from the camera."
                  : "Check your position."}
                <br />
                Eye Contact: {isLookingForward ? "Looking at Camera/Screen" : "Not Looking at Camera/Screen"}
              </Typography>
            ) : (
              <>
                <Typography sx={{ color: faceScore > 0 ? "orange" : "red", fontWeight: "bold" }}>
                  {/* ❌ No Face Detected */}
                  {faceScore > 0 ? "⚠️ Face Detected but not valid" : "❌ No Face Detected"}
                </Typography>
                <Typography
                  sx={{ color: faceScore > FACE_SCORE_ALLOWANCE * 100 ? "green" : "red", fontWeight: "bold" }}
                >
                  Visibility: {faceScore}% –{" "}
                  {faceScore < FACE_SCORE_ALLOWANCE * 100
                    ? "Face visibility is too low."
                    : faceScore <= FACE_SCORE_ALLOWANCE
                    ? "Face visibility is acceptable."
                    : "Face visibility is optimal."}
                </Typography>
                <Typography
                  sx={{ color: faceCoverage > FACE_COVERAGE_ALLOWANCE ? "green" : "red", fontWeight: "bold" }}
                >
                  Frame Coverage: {faceCoverage.toFixed(2)}% –{" "}
                  {faceCoverage < FACE_COVERAGE_ALLOWANCE
                    ? "Move closer to the camera."
                    : faceCoverage > FACE_COVERAGE_ALLOWANCE
                    ? "Face coverage is good."
                    : faceCoverage > 100
                    ? "Move slightly back from the camera."
                    : "Check your position."}
                </Typography>
                <Typography sx={{ color: isLookingForward ? "green" : "red", fontWeight: "bold" }}>
                  Eye Contact: {isLookingForward ? "Looking at Camera/Screen" : "Not Looking at Camera/Screen"}
                </Typography>
              </>
            )}
          </Box>

          {/* Log Stats Button */}
          <Button
            text="Log Detection Stats"
            color="btn2"
            onClick={handleLogStats}
            sx={{ mt: 2, display: "block", margin: "0 auto" }}
          >
            Log Detection Stats
          </Button>

          {/* Stats Table */}
          {showStats && stats && (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Stat</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell>Pass/Fail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Face Score</TableCell>
                    <TableCell>
                      {stats.faceScore}% (≥ {FACE_SCORE_ALLOWANCE * 100}%)
                    </TableCell>
                    <TableCell>{stats.faceScorePass ? "Pass" : "Fail"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Face Coverage</TableCell>
                    <TableCell>
                      {stats.faceCoverage}% (≥ {FACE_COVERAGE_ALLOWANCE}%)
                    </TableCell>
                    <TableCell>{stats.faceCoveragePass ? "Pass" : "Fail"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Eye Alignment</TableCell>
                    <TableCell>
                      {stats.eyeAlignmentPercentage} (&lt; {EYES_ALIGNED_ALLOWANCE})
                    </TableCell>
                    <TableCell>{stats.isEyesAligned ? "Pass" : "Fail"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>V-Shape Symmetry</TableCell>
                    <TableCell>
                      {stats.vSymmetryPercentage} (&lt; {V_SYMMETRICAL_ALLOWANCE})
                    </TableCell>
                    <TableCell>{stats.isVSymmetrical ? "Pass" : "Fail"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Nose Centered</TableCell>
                    <TableCell>
                      {stats.noseSymmetryPercentage} (&lt; {NOSE_CENTERED_ALLOWANCE})
                    </TableCell>
                    <TableCell>{stats.isNoseCentered ? "Pass" : "Fail"}</TableCell>
                  </TableRow>
                  {/* <TableRow>
                    <TableCell>Looking Forward</TableCell>
                    <TableCell>{stats.isLookingForward ? "Yes" : "No"}</TableCell>
                    <TableCell>{stats.isLookingForward ? "Pass" : "Fail"}</TableCell>
                  </TableRow> */}
                  <TableRow>
                    <TableCell>Multiple Faces</TableCell>
                    <TableCell>{stats.multipleFacesDetected ? "Yes" : "No"}</TableCell>
                    <TableCell>{!stats.multipleFacesDetected ? "Pass" : "Fail"}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* <Button
            text="Close"
            color="btn1"
            onClick={() => onClose()}
            sx={{ mt: 2, display: "block", margin: "0 auto" }}
          >
            Close
          </Button> */}
        </Box>
      }
      fullWidth={true}
      maxWidth="lg" // Adjust width as needed
    />
  );
};

export default FaceDetection;
